'use client'

import { useForm, useWatch } from 'react-hook-form'
import { ChangeEvent, useState } from 'react'
import { Button, Form, TextField, RangeSlider, Modal, TextFieldProps } from '@boltenergy-be/design-system'
import { calculateInputs, calculateLogarithmicPosition, calculateLogarithmicValue } from './utils'
import { SharesInputsWithOptionalShares } from './types'
import { MAX_SHARES, MAX_SLIDER_POS, MIN_SHARES, MIN_SLIDER_POS } from './constants'
import styles from './SharesForm.module.scss'
import ReturnOnInvestmentTable from './ReturnOnInvestmentTable'
import { Trans, useTranslation } from 'next-i18next/pages'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'
import Link from 'components/link/Link'
import { PLENTY_INFO_NOTE } from 'constants/constants'
import { useGetLanguage } from 'hooks/useGetLanguage'

const DEFAULT_SHARES = 4

const SharesForm = () => {
  // i18n
  const { t } = useTranslation(['boltXPlenty', 'validation', 'common'])
  const language = useGetLanguage()

  // Local state
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false)
  const [nonLogarithmicShares, setNonLogarithmicShares] = useState<number>(
    calculateLogarithmicPosition(
      DEFAULT_SHARES,
      { min: MIN_SLIDER_POS, max: MAX_SLIDER_POS },
      {
        min: MIN_SHARES,
        max: MAX_SHARES
      }
    )
  )

  // React Hook Form
  const { register, setValue, formState, control } = useForm<SharesInputsWithOptionalShares>({
    defaultValues: calculateInputs(DEFAULT_SHARES)
  })
  const watchShares = useWatch({ control, name: 'shares' })
  const watchTotalPrice = useWatch({ control, name: 'totalPrice' })
  const watchExpectedReturnPlentyOnly = useWatch({ control, name: 'expectedReturnPlentyOnly' })
  const watchExpectedReturnPlentyWithBolt = useWatch({ control, name: 'expectedReturnPlentyWithBolt' })

  // Constants
  const modalSuffix: TextFieldProps<'text'>['suffix'] = {
    as: (
      <Button
        className={styles['modal-trigger']}
        type="button"
        variant="tertiary"
        leadingIcon="questionMarkCircle"
        size="small"
        onClick={() => setInfoModalOpen(true)}
      />
    )
  }

  /**
   * calculate the new inputs and set the designated values in RHF
   * @param {number} shares
   */
  const setSharesInputs = (shares: number) => {
    const { totalPrice, expectedReturnPlentyOnly, expectedReturnPlentyWithBolt } = calculateInputs(shares)

    setValue('shares', shares, { shouldValidate: true })
    setValue('totalPrice', totalPrice, { shouldValidate: true })
    setValue('expectedReturnPlentyOnly', expectedReturnPlentyOnly, { shouldValidate: true })
    setValue('expectedReturnPlentyWithBolt', expectedReturnPlentyWithBolt, { shouldValidate: true })
  }

  /**
   * Handles the slider change event
   * @param {number} value
   */
  const handleChangeSlider = (value: number) => {
    setNonLogarithmicShares(value)

    const shares =
      calculateLogarithmicValue(
        value,
        { min: MIN_SLIDER_POS, max: MAX_SLIDER_POS },
        {
          min: MIN_SHARES,
          max: MAX_SHARES
        }
      ) || 1

    setSharesInputs(shares)
  }

  /**
   * Handles the amount change event
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
    const uncappedShares = e.target.valueAsNumber

    if (isNaN(uncappedShares)) {
      setValue('shares', null, { shouldValidate: true })
      return
    }

    const shares = uncappedShares > MAX_SHARES ? MAX_SHARES : uncappedShares < MIN_SHARES ? MIN_SHARES : uncappedShares

    setSharesInputs(shares)

    const sliderPosition = calculateLogarithmicPosition(
      shares,
      { min: MIN_SLIDER_POS, max: MAX_SLIDER_POS },
      { min: MIN_SHARES, max: MAX_SHARES }
    )
    setNonLogarithmicShares(sliderPosition)
  }

  return (
    <>
      <Form className={styles.form}>
        <RangeSlider
          label={t('shares.form.slider.label')}
          name="nonLogarithmicShares"
          min={MIN_SLIDER_POS}
          max={MAX_SLIDER_POS}
          step={1}
          value={nonLogarithmicShares}
          onChange={handleChangeSlider}
          className={styles.range}
        />

        <TextField
          {...register('shares', {
            required: t('validation:required'),
            min: t('validation:min', { min: MIN_SHARES }),
            max: t('validation:max', { max: MAX_SHARES })
          })}
          min={MIN_SHARES}
          max={MAX_SHARES}
          label={t('shares.form.amount.label')}
          type="number"
          value={watchShares || ''}
          onChange={handleChangeAmount}
          layoutClassName={styles.shares}
          error={formState?.errors?.shares?.message}
        />

        <TextField
          disabled
          readOnly
          name="totalPrice"
          label={t('shares.form.total.label')}
          value={t('common:formattedValue.currency', { value: watchTotalPrice })}
          layoutClassName={styles.total}
        />

        <TextField
          disabled
          name="expectedReturnPlentyWithBolt"
          label={t('shares.form.expectedRoi.label')}
          value={t('common:formattedValue.pricePerYear', { value: watchExpectedReturnPlentyWithBolt })}
          suffix={modalSuffix}
        />

        <Form.Footer className="flex-col gap-400">
          <StartSimulationButton isFullwidth />

          <small className={styles.risk}>
            <Trans
              {...{ t }}
              i18nKey="shares.form.riskText"
              components={[<Link key="0" variant="inline" target="_blank" as="a" href={PLENTY_INFO_NOTE[language]} />]}
            />
          </small>
        </Form.Footer>
      </Form>

      <Modal className={styles.modal} isOpen={infoModalOpen} onRequestClose={() => setInfoModalOpen(false)} title={t('shares.modal.title')}>
        <p>{t('shares.modal.description')}</p>

        <ReturnOnInvestmentTable
          shares={watchShares || 1}
          expectedReturnPlentyOnly={watchExpectedReturnPlentyOnly}
          expectedReturnPlentyWithBolt={watchExpectedReturnPlentyWithBolt}
        />
      </Modal>
    </>
  )
}

export default SharesForm
